import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Patient } from '../services/patientApi';
import { Appointment } from '../services/appointmentApi';
import { MedicalRecord } from '../services/api/medicalRecordsApi';
import { LabOrder } from '../types/labOrder';
import { getLocalizedValue } from './localization';
import { useAuthStore } from '../stores/useAuthStore';
import {
  refreshBrandAssets,
  getBrand,
  drawEditorialHeader,
  drawEditorialFooter,
  addDraftWatermark,
  downloadAndPrintPdf,
  lightTint,
  INK,
  MUTED,
} from './pdfBrand';
import {
  formatCalendarDate,
  formatDate,
  formatDateTime,
  type DateInput,
} from '@/lib/datetime';
import { getDisplayTimeZone } from '@/stores/useTimezoneStore';

/**
 * Date formatters for PDFs. These render outside React, so they read the
 * chosen display zone from the store directly (not the useDateFormat hook),
 * so printed documents match what the user sees on screen.
 *
 * `fmtDateTz`/`fmtDateTimeTz` are for `timestamp` instants (UTC → display
 * zone). `fmtCalDate` is for `date`-column calendar dates (DOB, due dates) —
 * timezone-less, so the day never shifts across midnight for distant viewers.
 */
const fmtDateTz = (v: DateInput) => formatDate(v, getDisplayTimeZone());
const fmtDateTimeTz = (v: DateInput) => formatDateTime(v, getDisplayTimeZone());
const fmtCalDate = (v: DateInput) => formatCalendarDate(v);
import type { Template, TemplateField, TemplateSection } from '../types/template';
import type { MultilingualField } from '../types/localization';

const calculateAge = (dob?: string): number | null => {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
};

const formatPatientMrn = (id: string): string =>
  id ? id.slice(-6).toUpperCase() : '—';

export const exportElementToPDF = async (elementId: string, filename: string = 'document.pdf') => {
  const element = document.getElementById(elementId);
  if (!element) {
    console.error(`Element with id ${elementId} not found`);
    return;
  }

  try {
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth - 20; // 10mm margins
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 10; // top margin

    // Add first page
    pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
    heightLeft -= pageHeight - 20; // account for margins

    // Add additional pages if needed
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= pageHeight - 20;
    }

    downloadAndPrintPdf(pdf, filename);
  } catch (error) {
    console.error('Error generating PDF:', error);
  }
};

/** ============================================================
 *  Patient summary PDF — redesigned.
 *
 *  Goal: a professional-looking handoff document. Replaces the prior
 *  label/value column layout with a hero identity card (avatar
 *  monogram + chips for MRN/DOB/age/gender), a two-column key-facts
 *  card grid, and dedicated callout blocks for safety-critical
 *  fields (allergies = red border, problems = blue border).
 *  ============================================================ */
export const exportPatientToPDF = async (
  patient: Patient,
  _language: 'en' | 'ar' = 'en',
) => {
  const pdf = new jsPDF();
  const brand = await refreshBrandAssets();
  const [pr, pg, pb] = brand.primary;
  const tint = lightTint(brand.primary, 0.93);

  const M = 14;
  const pageW = pdf.internal.pageSize.getWidth();
  const contentW = pageW - M * 2;

  const fullName = `${patient.firstName ?? ''} ${patient.lastName ?? ''}`.trim() || '—';
  const mrn = patient.medicalRecordNumber?.trim() || formatPatientMrn(patient.id);
  const age = calculateAge(patient.dateOfBirth);
  const dob = patient.dateOfBirth ? new Date(patient.dateOfBirth) : null;
  const dobLabel = dob && !Number.isNaN(dob.getTime()) ? fmtCalDate(dob) : '—';

  let y = drawEditorialHeader(pdf, {
    eyebrow: 'Patient',
    docTitle: 'Patient Record',
    subtitle: `${fullName} · MRN ${mrn}`,
  });

  // ── Hero identity card — brand-tinted, matching the Rx facts card ──
  const heroH = 32;
  pdf.setFillColor(tint[0], tint[1], tint[2]);
  pdf.roundedRect(M, y, contentW, heroH, 2, 2, 'F');

  const monoR = 10;
  const monoX = M + 8 + monoR;
  const monoY = y + heroH / 2;
  pdf.setFillColor(pr, pg, pb);
  pdf.circle(monoX, monoY, monoR, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(13);
  const initials =
    `${(patient.firstName ?? '?')[0] ?? '?'}${(patient.lastName ?? '?')[0] ?? ''}`.toUpperCase();
  pdf.text(initials, monoX, monoY, { align: 'center', baseline: 'middle' });

  const nameX = monoX + monoR + 7;
  pdf.setTextColor(INK[0], INK[1], INK[2]);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(15);
  pdf.text(fullName, nameX, y + 13);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8.5);
  pdf.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
  const subParts: string[] = [`MRN ${mrn}`];
  if (age !== null) subParts.push(`${age} y`);
  if (patient.gender) subParts.push(String(patient.gender).replace(/^\w/, (c) => c.toUpperCase()));
  if (patient.bloodType) subParts.push(patient.bloodType);
  pdf.text(subParts.join('  ·  '), nameX, y + 20);

  if (patient.assignedProvider) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
    pdf.text('ASSIGNED PROVIDER', pageW - M - 6, y + 13, { align: 'right' });
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(INK[0], INK[1], INK[2]);
    pdf.text(
      `${patient.assignedProvider.firstName} ${patient.assignedProvider.lastName}`,
      pageW - M - 6,
      y + 20,
      { align: 'right' },
    );
  }
  y += heroH + 6;

  // ── Facts card — single brand-tinted card, 2-col grid (Rx style) ──
  const facts: Array<[string, string]> = [
    ['Date of birth', dobLabel],
    ['Age', age !== null ? `${age} years` : '—'],
    ['Phone', patient.phone || '—'],
    ['Email', patient.email || '—'],
    ['Address', patient.address || '—'],
    ['Preferred language', patient.preferredLanguage || '—'],
  ];
  const factRows = Math.ceil(facts.length / 2);
  const factCardH = 8 + factRows * 12;
  pdf.setFillColor(tint[0], tint[1], tint[2]);
  pdf.roundedRect(M, y, contentW, factCardH, 2, 2, 'F');
  const colX = [M + 6, pageW / 2 + 2];
  const colValW = contentW / 2 - 12;
  facts.forEach(([label, value], i) => {
    const x = colX[i % 2];
    const ry = y + 8 + Math.floor(i / 2) * 12;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
    pdf.text(label.toUpperCase(), x, ry);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10.5);
    pdf.setTextColor(INK[0], INK[1], INK[2]);
    const v = pdf.splitTextToSize(value || '—', colValW);
    pdf.text(v.slice(0, 1), x, ry + 5);
  });
  y += factCardH + 6;

  // ── Emergency contact strip ──
  const ecName = patient.emergencyContactName?.trim();
  const ecPhone = patient.emergencyContactPhone?.trim();
  if (ecName || ecPhone) {
    const ecLine = [ecName, ecPhone].filter(Boolean).join('  ·  ');
    pdf.setFillColor(tint[0], tint[1], tint[2]);
    pdf.roundedRect(M, y, contentW, 14, 2, 2, 'F');
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
    pdf.text('EMERGENCY CONTACT', M + 6, y + 5.5);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10.5);
    pdf.setTextColor(INK[0], INK[1], INK[2]);
    pdf.text(ecLine, M + 6, y + 11);
    y += 18;
  }

  // ── Section heading — brand tick + title, Rx palette ──
  const heading = (title: string) => {
    if (y > 255) {
      pdf.addPage();
      y = drawEditorialHeader(pdf);
    }
    pdf.setFillColor(pr, pg, pb);
    pdf.rect(M, y, 1.4, 6, 'F');
    pdf.setTextColor(INK[0], INK[1], INK[2]);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(13);
    pdf.text(title, M + 5, y + 5);
    y += 9;
  };

  // ── Allergies callout (red box when present, brand-tint when clear) ──
  const allergiesRaw = (patient.allergies ?? '').trim();
  const hasAllergies = allergiesRaw && !/^(none|nkda|n\/a|no known)/i.test(allergiesRaw);
  heading('Safety & alerts');
  {
    const items = hasAllergies
      ? allergiesRaw.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean)
      : [];
    const text = items.length ? items.map((a) => `• ${a}`).join('\n') : 'No known allergies';
    const lines = pdf.splitTextToSize(text, contentW - 14);
    const boxH = Math.max(14, lines.length * 5 + 8);
    if (hasAllergies) {
      pdf.setFillColor(254, 242, 242);
      pdf.setDrawColor(220, 38, 38);
      pdf.setLineWidth(0.4);
      pdf.roundedRect(M, y, contentW, boxH, 2, 2, 'FD');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.setTextColor(185, 28, 28);
      pdf.text('ALLERGIES', M + 5, y + 6);
    } else {
      pdf.setFillColor(tint[0], tint[1], tint[2]);
      pdf.roundedRect(M, y, contentW, boxH, 2, 2, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
      pdf.text('ALLERGIES', M + 5, y + 6);
    }
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9.5);
    pdf.setTextColor(INK[0], INK[1], INK[2]);
    pdf.text(lines, M + 5, y + 12);
    y += boxH + 5;
  }

  // ── Problem list / history ──
  heading('Active problems & history');
  const historyItems = Array.isArray(patient.medicalHistory)
    ? patient.medicalHistory.filter(Boolean)
    : [];
  {
    const text = historyItems.length
      ? historyItems.map((h) => `• ${h}`).join('\n')
      : 'No conditions on file';
    const lines = pdf.splitTextToSize(text, contentW - 14);
    const boxH = Math.max(14, lines.length * 5 + 8);
    pdf.setFillColor(tint[0], tint[1], tint[2]);
    pdf.roundedRect(M, y, contentW, boxH, 2, 2, 'F');
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9.5);
    pdf.setTextColor(INK[0], INK[1], INK[2]);
    pdf.text(lines, M + 5, y + 7);
    y += boxH + 5;
  }

  // ── Lifestyle (only if any non-empty) ──
  const lifestyleFields: Array<[string, string | undefined | null]> = [
    ['Smoking', patient.smokingStatus],
    ['Alcohol use', patient.alcoholUse],
    ['Exercise', patient.exerciseLevel],
    ['Occupation', patient.occupation],
    ['Employer', patient.employer],
    ['Substance use', patient.substanceUse],
  ];
  const lifestyleRows = lifestyleFields.filter(([, v]) => v && String(v).trim());
  if (lifestyleRows.length > 0) {
    heading('Lifestyle & social');
    for (const [label, value] of lifestyleRows) {
      if (y > 270) {
        pdf.addPage();
        y = drawEditorialHeader(pdf);
      }
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
      pdf.text(label, M, y);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(INK[0], INK[1], INK[2]);
      pdf.text(String(value), M + 38, y);
      y += 6;
    }
    y += 3;
  }

  // ── Notes ──
  if (patient.notes?.trim()) {
    heading('Notes');
    const lines = pdf.splitTextToSize(patient.notes.trim(), contentW);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9.5);
    pdf.setTextColor(INK[0], INK[1], INK[2]);
    pdf.text(lines, M, y);
    y += lines.length * 5 + 2;
  }

  drawEditorialFooter(pdf, { confidential: true });

  const safeName = fullName.replace(/[^a-z0-9]+/gi, '_').toLowerCase() || 'patient';
  downloadAndPrintPdf(pdf, `${safeName}_${mrn}.pdf`);
};

export interface DownloadMedicalRecordOptions {
  /**
   * Required template fields that were left empty when the record was
   * signed. When provided, the PDF prints a "signed with gaps" notice
   * listing them so the downstream reader (printed chart, faxed copy,
   * payer review) can see the encounter wasn't fully documented.
   */
  gaps?: Array<{ key: string; label: string }>;
  /**
   * Template the encounter was authored against. When provided, the PDF
   * iterates the template's sections/fields and reads values from
   * record.templateData[fieldId] — that's how specialty templates get
   * faithful printouts (a cardiology template's "ECG findings" section
   * appears, an OB/GYN template's "Estimated gestational age" appears).
   * When omitted (legacy SOAP_SEED_ID record or unknown template), the
   * PDF falls back to the hardcoded SOAP block.
   */
  template?: Template | null;
}

const localizeMultilingual = (
  v: MultilingualField | string | undefined | null,
  language: 'en' | 'ar',
): string => {
  if (!v) return '';
  if (typeof v === 'string') return v.trim();
  if (language === 'ar') return (v.ar || v.en || '').trim();
  return (v.en || v.ar || '').trim();
};

/**
 * Format a template field's stored value into a printable string.
 * Mirrors the on-screen TemplateDrivenSections renderer so the PDF and
 * the chart agree on what each field looks like.
 */
const formatTemplateValue = (
  field: TemplateField,
  raw: unknown,
  language: 'en' | 'ar',
): string => {
  if (raw === undefined || raw === null) return '';
  if (typeof raw === 'string') return raw.trim();
  if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw);

  if (field.type === 'select' || field.type === 'radio') {
    const match = field.options?.find((o) => o.value === String(raw));
    if (match) return localizeMultilingual(match.label, language);
    return String(raw);
  }

  if (field.type === 'date' && typeof raw === 'string') {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? raw : fmtCalDate(d);
  }

  if (field.type === 'tags' && Array.isArray(raw)) {
    return raw
      .map((item) => (typeof item === 'string' ? item : JSON.stringify(item)))
      .filter(Boolean)
      .join(', ');
  }

  if (field.type === 'checkbox') {
    if (Array.isArray(raw)) {
      const labels = raw
        .map((v) => {
          const opt = field.options?.find((o) => o.value === String(v));
          return opt ? localizeMultilingual(opt.label, language) : String(v);
        })
        .filter(Boolean);
      return labels.join(', ');
    }
    return raw ? '✓' : '';
  }

  if (Array.isArray(raw)) {
    return raw
      .map((item) =>
        typeof item === 'string' || typeof item === 'number' ? String(item) : JSON.stringify(item),
      )
      .filter(Boolean)
      .join(', ');
  }

  if (typeof raw === 'object') {
    try {
      return JSON.stringify(raw);
    } catch {
      return '';
    }
  }
  return String(raw);
};

export const downloadMedicalRecord = async (
  record: MedicalRecord,
  patientName: string,
  language: 'en' | 'ar' = 'en',
  options: DownloadMedicalRecordOptions = {},
) => {
  const gaps = options.gaps ?? [];
  const template = options.template ?? null;
  const pdf = new jsPDF();
  await refreshBrandAssets();

  const visitDate = (() => {
    if (!record.visitDate) return null;
    const d = new Date(record.visitDate);
    return Number.isNaN(d.getTime()) ? null : d;
  })();
  const visitDateLabel = visitDate ? fmtDateTz(visitDate) : '—';
  const recordTypeLabel = (record.type || 'progress_note')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

  let y = drawEditorialHeader(pdf, {
    eyebrow: 'Clinical',
    docTitle: 'Encounter Note',
    subtitle: `${recordTypeLabel} · ${visitDateLabel}`,
    showConfidential: true,
  });

  const margin = 14;
  const pageW = pdf.internal.pageSize.getWidth();
  const contentW = pageW - margin * 2;
  const [pr, pg, pb] = getBrand().primary;

  // --- Hero visit card: patient + reference + status chip ---
  const heroH = 30;
  pdf.setFillColor(248, 250, 252);
  pdf.setDrawColor(228, 228, 232);
  pdf.setLineWidth(0.3);
  pdf.roundedRect(margin, y, contentW, heroH, 2.5, 2.5, 'FD');

  pdf.setTextColor(20, 20, 24);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(13);
  pdf.text(patientName || '—', margin + 6, y + 11);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8.5);
  pdf.setTextColor(110, 110, 120);
  const refLabel = record.id ? `REF ${record.id.slice(-8).toUpperCase()}` : 'REF —';
  pdf.text(`${refLabel}  ·  ${visitDateLabel}  ·  ${recordTypeLabel}`, margin + 6, y + 18);

  // Status chip (right-aligned in hero)
  const status = (record.status || 'draft').toLowerCase();
  const statusColor: [number, number, number] =
    status === 'signed' || status === 'amended'
      ? [16, 122, 90]
      : status === 'draft'
        ? [180, 100, 20]
        : [110, 110, 120];
  const statusBg: [number, number, number] =
    status === 'signed' || status === 'amended'
      ? [232, 246, 240]
      : status === 'draft'
        ? [255, 246, 226]
        : [240, 240, 244];
  const chipText = status.toUpperCase();
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8);
  const chipW = pdf.getTextWidth(chipText) + 10;
  const chipX = pageW - margin - 6 - chipW;
  pdf.setFillColor(statusBg[0], statusBg[1], statusBg[2]);
  pdf.roundedRect(chipX, y + 6, chipW, 7, 3, 3, 'F');
  pdf.setTextColor(statusColor[0], statusColor[1], statusColor[2]);
  pdf.text(chipText, chipX + chipW / 2, y + 11, { align: 'center' });

  if (record.signedAt) {
    const sd = new Date(record.signedAt);
    const signedLabel = Number.isNaN(sd.getTime()) ? record.signedAt : fmtDateTimeTz(sd);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(110, 110, 120);
    pdf.text(`Signed ${signedLabel}`, pageW - margin - 6, y + 21, { align: 'right' });
  }

  y += heroH + 8;

  const sectionHeading = (title: string) => {
    if (y > 255) {
      pdf.addPage();
      y = drawEditorialHeader(pdf);
    }
    pdf.setFillColor(pr, pg, pb);
    pdf.rect(margin, y, 1.2, 6, 'F');
    pdf.setTextColor(30, 30, 35);
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text(title.toUpperCase(), margin + 4, y + 5);
    y += 8;
    pdf.setDrawColor(228, 228, 232);
    pdf.setLineWidth(0.2);
    pdf.line(margin, y, pageW - margin, y);
    y += 4;
    pdf.setTextColor(0, 0, 0);
  };

  // SOAP / long-field block — each field gets a left tint rail so
  // sections read as distinct zones rather than a wall of text. The
  // label sits 5mm INSET from the rail so the colored bar never visually
  // collides with the uppercase label letters; body wraps at the same
  // inset to keep the "letter on the rail" rhythm.
  const labelInset = 5;
  const longField = (
    label: string,
    value: string | null | undefined,
    accent?: [number, number, number],
  ) => {
    const text = (value ?? '').trim();
    if (!text) return;
    // Compute the block height first so we can lay the rail down once,
    // sized to the full label+body.
    pdf.setFontSize(10);
    const lines = pdf.splitTextToSize(text, contentW - labelInset);
    const blockH = 6 + lines.length * 5 + 4; // label row + body + bottom pad
    if (y + blockH > 275) {
      pdf.addPage();
      y = drawEditorialHeader(pdf);
    }
    const rail = accent ?? ([pr, pg, pb] as [number, number, number]);
    // 1. rail
    pdf.setFillColor(rail[0], rail[1], rail[2]);
    pdf.rect(margin, y, 1.4, blockH - 2, 'F');
    // 2. label
    pdf.setFontSize(8.5);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(80, 80, 95);
    pdf.text(label.toUpperCase(), margin + labelInset, y + 4);
    // 3. body
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(28, 28, 32);
    pdf.text(lines, margin + labelInset, y + 10);
    y += blockH + 2;
  };

  // Gap notice — only meaningful on signed records, since drafts haven't
  // attested to anything yet. Box the notice so it stands out from the
  // surrounding label/value rows.
  const isSigned = record.status === 'signed' || record.status === 'amended';
  if (isSigned && gaps.length > 0) {
    if (y > 245) {
      pdf.addPage();
      y = drawEditorialHeader(pdf);
    }
    const gapList = gaps.map((g) => g.label).join(', ');
    const headline = `Signed with ${gaps.length} required field${gaps.length === 1 ? '' : 's'} missing`;
    const bodyLines = pdf.splitTextToSize(`Missing: ${gapList}`, contentW - 8);
    const boxH = 8 + 5 + bodyLines.length * 5 + 4;
    pdf.setDrawColor(200, 140, 30);
    pdf.setFillColor(255, 247, 224);
    pdf.roundedRect(margin, y, contentW, boxH, 2, 2, 'FD');
    pdf.setTextColor(140, 90, 10);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.text(headline, margin + 4, y + 6);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.text(bodyLines, margin + 4, y + 12);
    pdf.setTextColor(0, 0, 0);
    y += boxH + 4;
  }

  // SOAP block — each component is its own coloured-rail block (no
  // outer wrapping "Encounter Note" heading; the doc title already
  // says it). The four classic SOAP letters get distinct accents so
  // the reader's eye can find them at a glance.
  const SOAP_ACCENTS: Record<string, [number, number, number]> = {
    chief: [pr, pg, pb],
    subjective: [37, 99, 235],   // blue-600
    objective: [16, 122, 90],    // emerald-700
    assessment: [180, 100, 20],  // amber-700
    plan: [124, 58, 237],        // violet-600
    extra: [110, 110, 130],
  };

  // Per-section accent picker for arbitrary template sections.
  // Mirrors the on-screen accentForSection so chart and PDF agree.
  const sectionAccent = (id: string, idx: number): [number, number, number] => {
    const known: Record<string, [number, number, number]> = {
      subjective: SOAP_ACCENTS.subjective,
      objective: SOAP_ACCENTS.objective,
      assessment: SOAP_ACCENTS.assessment,
      plan: SOAP_ACCENTS.plan,
      'chief-complaint': SOAP_ACCENTS.chief,
      vitals: SOAP_ACCENTS.objective,
      'review-of-systems': SOAP_ACCENTS.plan,
      'physical-exam': SOAP_ACCENTS.objective,
      'history-of-present-illness': SOAP_ACCENTS.subjective,
    };
    if (known[id]) return known[id];
    const palette: [number, number, number][] = [
      SOAP_ACCENTS.subjective,
      SOAP_ACCENTS.objective,
      SOAP_ACCENTS.assessment,
      SOAP_ACCENTS.plan,
      SOAP_ACCENTS.extra,
    ];
    return palette[idx % palette.length];
  };

  // Template-driven render path: iterate the template's own sections
  // and fields, reading values from record.templateData. Falls back to
  // the SOAP block below when no template is supplied (legacy notes
  // and SOAP_SEED_ID records).
  const templateData = (record.templateData ?? {}) as Record<string, unknown>;
  const renderTemplateField = (
    field: TemplateField,
    sectionAcc: [number, number, number],
  ) => {
    // `<sectionId>_text` fields are the seeder convention for the big
    // freeform paragraph that holds an entire section's narrative —
    // render those WITHOUT the per-field label so they read as prose.
    const isPrimaryText = /_text$/.test(field.id);
    const value = formatTemplateValue(field, templateData[field.id], language);
    if (!value) return;
    const label = localizeMultilingual(field.name, language);
    if (isPrimaryText) {
      longField(label || 'Notes', value, sectionAcc);
    } else {
      longField(label || field.id, value, sectionAcc);
    }
  };

  const renderTemplateSection = (section: TemplateSection, idx: number) => {
    const accent = sectionAccent(section.id, idx);
    // Skip wholly empty sections.
    const fields = Array.isArray(section.fields) ? section.fields : [];
    const anyValue = fields.some((f) => {
      const v = formatTemplateValue(f, templateData[f.id], language);
      return v.length > 0;
    });
    if (!anyValue) return;
    const title = localizeMultilingual(section.title, language) || section.id;
    sectionHeading(title);
    fields.forEach((f) => renderTemplateField(f, accent));
  };

  const usedTemplate =
    template && Array.isArray(template.sections) && template.sections.length > 0;

  if (usedTemplate) {
    // Chief complaint is a discrete column on the record, not a template
    // field — surface it first if present (so a cardiology template's
    // section list doesn't bury "chest pain on exertion" four sections
    // in).
    if ((record.chiefComplaint ?? '').trim()) {
      sectionHeading('Chief Complaint');
      longField('Reason for visit', record.chiefComplaint, SOAP_ACCENTS.chief);
    }
    template!.sections.forEach((s, i) => renderTemplateSection(s, i));
  } else if (
    record.chiefComplaint ||
    record.subjective ||
    record.objective ||
    record.assessment ||
    record.plan
  ) {
    sectionHeading('SOAP Note');
    longField('Chief Complaint', record.chiefComplaint, SOAP_ACCENTS.chief);
    longField('Subjective', record.subjective, SOAP_ACCENTS.subjective);
    longField('Objective', record.objective, SOAP_ACCENTS.objective);
    longField('Assessment', record.assessment, SOAP_ACCENTS.assessment);
    longField('Plan', record.plan, SOAP_ACCENTS.plan);
  }

  // Background — historical/contextual fields populated outside SOAP.
  if (
    record.historyPresentIllness ||
    record.pastMedicalHistory ||
    record.familyHistory ||
    record.socialHistory ||
    record.reviewOfSystems ||
    record.physicalExam
  ) {
    sectionHeading('Background');
    longField('History of Present Illness', record.historyPresentIllness);
    longField('Past Medical History', record.pastMedicalHistory);
    longField('Family History', record.familyHistory);
    longField('Social History', record.socialHistory);
    longField('Review of Systems', record.reviewOfSystems);
    longField('Physical Exam', record.physicalExam);
  }

  // Diagnoses — show as chip-style rows (code badge + description).
  if (Array.isArray(record.diagnoses) && record.diagnoses.length > 0) {
    sectionHeading('Diagnoses');
    record.diagnoses.forEach((dx) => {
      if (y > 268) {
        pdf.addPage();
        y = drawEditorialHeader(pdf);
      }
      const codeText = dx.code ? `${dx.type || 'ICD-10'} ${dx.code}` : '';
      if (codeText) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        const w = pdf.getTextWidth(codeText) + 6;
        pdf.setFillColor(232, 234, 248);
        pdf.roundedRect(margin, y - 1, w, 6, 1.5, 1.5, 'F');
        pdf.setTextColor(60, 60, 110);
        pdf.text(codeText, margin + 3, y + 3);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(28, 28, 32);
        const lines = pdf.splitTextToSize(dx.description ?? '', contentW - w - 6);
        pdf.text(lines, margin + w + 4, y + 3);
        y += Math.max(7, lines.length * 5 + 2);
      } else {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(28, 28, 32);
        const lines = pdf.splitTextToSize(`• ${dx.description ?? ''}`, contentW - 4);
        pdf.text(lines, margin + 2, y);
        y += lines.length * 5 + 2;
      }
    });
    y += 2;
  }

  // Medications + allergies as separate accented blocks.
  if ((record.medications ?? '').trim() || (record.allergies ?? '').trim()) {
    sectionHeading('Medications & Allergies');
    longField('Current Medications', record.medications, [16, 122, 90]);
    longField('Allergies', record.allergies, [220, 38, 38]);
  }

  // Vital signs — card grid (Temp/BP/HR/RR/SpO₂/Wt/Ht/BMI). The latest
  // reading gets its own card group; earlier readings list compactly.
  if (Array.isArray(record.vitalSigns) && record.vitalSigns.length > 0) {
    sectionHeading('Vital Signs');
    const sorted = [...record.vitalSigns].sort((a, b) => {
      const ta = new Date(a.measuredAt || 0).getTime();
      const tb = new Date(b.measuredAt || 0).getTime();
      return tb - ta;
    });
    const latest = sorted[0];

    const measured = (v: { measuredAt?: string }) =>
      v.measuredAt
        ? (() => {
            const d = new Date(v.measuredAt!);
            return Number.isNaN(d.getTime()) ? v.measuredAt! : fmtDateTimeTz(d);
          })()
        : '—';

    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(110, 110, 120);
    pdf.text(`Latest reading · ${measured(latest)}`, margin, y);
    y += 4;

    type Cell = { label: string; value: string } | null;
    const cells: Cell[] = [
      latest.temperature != null
        ? { label: 'Temp', value: `${latest.temperature}°${latest.temperatureUnit === 'fahrenheit' ? 'F' : 'C'}` }
        : null,
      latest.bloodPressureSystolic != null && latest.bloodPressureDiastolic != null
        ? { label: 'BP', value: `${latest.bloodPressureSystolic}/${latest.bloodPressureDiastolic}` }
        : null,
      latest.heartRate != null ? { label: 'HR', value: `${latest.heartRate}` } : null,
      latest.respiratoryRate != null ? { label: 'RR', value: `${latest.respiratoryRate}` } : null,
      latest.oxygenSaturation != null ? { label: 'SpO₂', value: `${latest.oxygenSaturation}%` } : null,
      latest.weight != null ? { label: 'Weight', value: `${latest.weight} ${latest.weightUnit ?? 'kg'}` } : null,
      latest.height != null ? { label: 'Height', value: `${latest.height} ${latest.heightUnit ?? 'cm'}` } : null,
      latest.bmi != null ? { label: 'BMI', value: `${latest.bmi}` } : null,
    ];
    const filled = cells.filter((c): c is { label: string; value: string } => Boolean(c));

    const cols = 4;
    const cardW = (contentW - (cols - 1) * 4) / cols;
    const cardH = 16;
    filled.forEach((cell, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const cx = margin + col * (cardW + 4);
      const cy = y + row * (cardH + 3);
      pdf.setDrawColor(228, 228, 232);
      pdf.setFillColor(252, 252, 254);
      pdf.setLineWidth(0.3);
      pdf.roundedRect(cx, cy, cardW, cardH, 1.5, 1.5, 'FD');
      pdf.setFillColor(pr, pg, pb);
      pdf.rect(cx, cy + 2, 1, cardH - 4, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(7);
      pdf.setTextColor(120, 120, 130);
      pdf.text(cell.label.toUpperCase(), cx + 4, cy + 6);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.setTextColor(28, 28, 32);
      pdf.text(cell.value, cx + 4, cy + 12.5);
    });
    if (filled.length > 0) {
      y += Math.ceil(filled.length / cols) * (cardH + 3) + 2;
    }

    if (latest.notes) {
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(110, 110, 120);
      const lines = pdf.splitTextToSize(`Notes: ${latest.notes}`, contentW);
      pdf.text(lines, margin, y);
      y += lines.length * 5 + 2;
    }

    // Earlier readings compact list.
    const earlier = sorted.slice(1);
    if (earlier.length > 0) {
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(110, 110, 120);
      pdf.text('Earlier readings', margin, y);
      y += 4;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(40, 40, 50);
      earlier.forEach((v) => {
        if (y > 270) {
          pdf.addPage();
          y = drawEditorialHeader(pdf);
        }
        const parts: string[] = [];
        if (v.temperature != null) parts.push(`T ${v.temperature}°${v.temperatureUnit === 'fahrenheit' ? 'F' : 'C'}`);
        if (v.bloodPressureSystolic != null && v.bloodPressureDiastolic != null)
          parts.push(`BP ${v.bloodPressureSystolic}/${v.bloodPressureDiastolic}`);
        if (v.heartRate != null) parts.push(`HR ${v.heartRate}`);
        if (v.respiratoryRate != null) parts.push(`RR ${v.respiratoryRate}`);
        if (v.oxygenSaturation != null) parts.push(`SpO₂ ${v.oxygenSaturation}%`);
        if (v.weight != null) parts.push(`Wt ${v.weight}${v.weightUnit ?? 'kg'}`);
        const text = `${measured(v)} · ${parts.join('  ·  ')}`;
        const lines = pdf.splitTextToSize(text, contentW);
        pdf.text(lines, margin, y);
        y += lines.length * 5 + 1;
      });
    }
    y += 2;
  }

  longField('Procedures', record.procedures);
  longField('Additional Notes', record.notes);

  // Signed-by attestation block, only on signed records.
  if ((record.status === 'signed' || record.status === 'amended') && record.signedBy) {
    if (y > 250) {
      pdf.addPage();
      y = drawEditorialHeader(pdf);
    }
    const attestH = 22;
    pdf.setDrawColor(16, 122, 90);
    pdf.setFillColor(240, 250, 245);
    pdf.setLineWidth(0.4);
    pdf.roundedRect(margin, y, contentW, attestH, 2, 2, 'FD');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(16, 122, 90);
    pdf.text('ELECTRONICALLY SIGNED', margin + 5, y + 6);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9.5);
    pdf.setTextColor(28, 28, 32);
    const sb = record.signedBy as
      | string
      | { provider?: { firstName?: string; lastName?: string }; firstName?: string; lastName?: string }
      | null;
    const signedName =
      typeof sb === 'string'
        ? sb
        : sb?.provider
          ? `${sb.provider.firstName ?? ''} ${sb.provider.lastName ?? ''}`.trim() || '—'
          : `${sb?.firstName ?? ''} ${sb?.lastName ?? ''}`.trim() || '—';
    pdf.text(`Signed by ${signedName}`, margin + 5, y + 13);
    if (record.signedAt) {
      const sd = new Date(record.signedAt);
      pdf.setFontSize(8.5);
      pdf.setTextColor(110, 110, 120);
      pdf.text(
        Number.isNaN(sd.getTime()) ? record.signedAt : fmtDateTimeTz(sd),
        margin + 5,
        y + 18,
      );
    }
    y += attestH + 4;
  }

  drawEditorialFooter(pdf, { confidential: true });
  // Draft watermark goes LAST so it overlays content + footer rule on
  // every page (including ones added mid-render by section helpers).
  if ((record.status || 'draft') === 'draft') {
    addDraftWatermark(pdf);
  }

  const safePatientName = (patientName || 'patient').replace(/[^a-z0-9]+/gi, '_').toLowerCase();
  const datePart = visitDate ? visitDate.toISOString().split('T')[0] : 'undated';
  const draftSuffix = (record.status || 'draft') === 'draft' ? '_DRAFT' : '';
  downloadAndPrintPdf(pdf, `encounter_${safePatientName}_${datePart}${draftSuffix}.pdf`);
};

// Keep the old function name for backward compatibility
export const exportMedicalRecordToPDF = downloadMedicalRecord;

export const exportLabOrderToPDF = async (order: LabOrder, patientName: string, language: 'en' | 'ar') => {
  const pdf = new jsPDF();
  await refreshBrandAssets();

  let yPosition = drawEditorialHeader(pdf, {
    eyebrow: 'Laboratory',
    docTitle: 'Lab Order',
    subtitle: patientName,
  });

  // Order details
  pdf.setFontSize(12);

  const addField = (label: string, value: string) => {
    pdf.setFont('helvetica', 'bold');
    pdf.text(label + ':', 20, yPosition);
    pdf.setFont('helvetica', 'normal');
    pdf.text(value, 70, yPosition);
    yPosition += 10;
  };

  addField('Order ID', order.id);
  addField('Patient', patientName);
  addField('Order Date', fmtDateTz(order.orderDate));
  addField('Status', order.status);
  addField('Priority', order.priority);
  addField('Ordered By', order.orderedBy);
  if (order.requiredBy) {
    addField('Required By', fmtDateTz(order.requiredBy));
  }

  // Clinical Information
  yPosition += 10;
  pdf.setFont('helvetica', 'bold');
  pdf.text('Clinical Information:', 20, yPosition);
  yPosition += 7;
  pdf.setFont('helvetica', 'normal');
  const clinicalLines = pdf.splitTextToSize(getLocalizedValue(order.clinicalInfo, language), 170);
  pdf.text(clinicalLines, 20, yPosition);
  yPosition += clinicalLines.length * 5 + 5;

  // Provisional Diagnosis
  if (order.provisionalDiagnosis) {
    pdf.setFont('helvetica', 'bold');
    pdf.text('Provisional Diagnosis:', 20, yPosition);
    yPosition += 7;
    pdf.setFont('helvetica', 'normal');
    const diagnosisLines = pdf.splitTextToSize(getLocalizedValue(order.provisionalDiagnosis, language), 170);
    pdf.text(diagnosisLines, 20, yPosition);
    yPosition += diagnosisLines.length * 5 + 5;
  }

  // Ordered Tests
  pdf.setFont('helvetica', 'bold');
  pdf.text('Ordered Tests:', 20, yPosition);
  yPosition += 10;

  // Table header
  pdf.setFont('helvetica', 'bold');
  pdf.text('Test Name', 20, yPosition);
  pdf.text('Category', 80, yPosition);
  pdf.text('Specimen', 120, yPosition);
  pdf.text('Result', 160, yPosition);
  yPosition += 7;

  // Table content
  pdf.setFont('helvetica', 'normal');
  order.tests.forEach(test => {
    pdf.text(test.name, 20, yPosition);
    pdf.text(test.category, 80, yPosition);
    pdf.text(test.specimen, 120, yPosition);
    pdf.text(test.result || 'Pending', 160, yPosition);
    yPosition += 7;

    // Check if we need a new page
    if (yPosition > 270) {
      pdf.addPage();
      yPosition = 20;
    }
  });

  // Special Instructions
  if (order.specialInstructions) {
    yPosition += 10;
    pdf.setFont('helvetica', 'bold');
    pdf.text('Special Instructions:', 20, yPosition);
    yPosition += 7;
    pdf.setFont('helvetica', 'normal');
    const instructionLines = pdf.splitTextToSize(getLocalizedValue(order.specialInstructions, language), 170);
    pdf.text(instructionLines, 20, yPosition);
  }

  // Footer
  drawEditorialFooter(pdf);

  downloadAndPrintPdf(pdf, `lab_order_${order.id}_${Date.now()}.pdf`);
};

export interface ReferralLetterData {
  specialty: string;
  reason: string;
  priority?: 'routine' | 'urgent' | 'stat';
  /** Optional clinical context (typically the encounter assessment). */
  clinicalContext?: string;
  /** Optional addressee — a named doctor / facility / contact + needed-by date. */
  referredToName?: string | null;
  referredToFacility?: string | null;
  referredToContact?: string | null;
  neededBy?: string | null;
  /**
   * Who signed the letter, when that is not whoever is printing it.
   *
   * A referral is signed by the doctor who wrote it and can be printed at the
   * desk, from the patient's file, days later. Reading the signature off the
   * current session put the receptionist's name under a clinical letter.
   */
  signedBy?: string | null;
}

/**
 * Referral letter — a printable, branded letter the doctor hands to the
 * patient (or faxes) to refer them to a specialist. Deliberately a
 * *letter*, not an order form: addressee, salutation, prose body,
 * signature. The clinic letterhead is read from the theme store; the
 * referring provider is `referral.signedBy` when the letter carries one —
 * a referral is printed at the desk and from the file, days after it was
 * signed, and the signature belongs to whoever signed it.
 */
export const exportReferralToPDF = async (
  referral: ReferralLetterData,
  patient: Patient,
  _language: 'en' | 'ar' = 'en',
) => {
  const pdf = new jsPDF();
  await refreshBrandAssets();

  const provider = useAuthStore.getState().user;
  const providerName =
    referral.signedBy?.trim() ||
    (provider && `${provider.firstName ?? ''} ${provider.lastName ?? ''}`.trim()) ||
    '';

  const fullName =
    `${patient.firstName ?? ''} ${patient.lastName ?? ''}`.trim() || '—';
  const mrn = patient.medicalRecordNumber?.trim() || formatPatientMrn(patient.id);
  const age = calculateAge(patient.dateOfBirth);
  const today = fmtDateTz(new Date());

  let y = drawEditorialHeader(pdf, {
    eyebrow: 'Referral',
    docTitle: 'Referral Letter',
    subtitle: `${referral.specialty} · ${today}`,
    showConfidential: true,
  });

  const margin = 14;
  const pageW = pdf.internal.pageSize.getWidth();
  const contentW = pageW - margin * 2;
  const [pr, pg, pb] = getBrand().primary;

  // Priority callout for non-routine referrals.
  const priority = (referral.priority || 'routine').toLowerCase();
  if (priority === 'urgent' || priority === 'stat') {
    const isStat = priority === 'stat';
    pdf.setDrawColor(isStat ? 220 : 200, isStat ? 38 : 140, isStat ? 38 : 30);
    pdf.setFillColor(isStat ? 254 : 255, isStat ? 242 : 247, isStat ? 242 : 224);
    pdf.setLineWidth(0.4);
    pdf.roundedRect(margin, y, contentW, 10, 2, 2, 'FD');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(isStat ? 153 : 140, isStat ? 27 : 90, isStat ? 27 : 10);
    pdf.text(`${priority.toUpperCase()} REFERRAL`, margin + 5, y + 6.5);
    pdf.setTextColor(0, 0, 0);
    y += 14;
  }

  // Addressee + subject line. Prefer a named recipient when supplied, falling
  // back to the bare "<specialty> Specialist". Facility / contact / needed-by
  // print underneath as a small addressee block when present.
  const addressee =
    referral.referredToName?.trim() || `${referral.specialty} Specialist`;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(28, 28, 32);
  pdf.text(`To: ${addressee}`, margin, y);
  y += 6;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9.5);
  pdf.setTextColor(90, 90, 100);
  const addresseeLines: string[] = [];
  if (referral.referredToName?.trim())
    addresseeLines.push(`${referral.specialty}`);
  if (referral.referredToFacility?.trim())
    addresseeLines.push(referral.referredToFacility.trim());
  if (referral.referredToContact?.trim())
    addresseeLines.push(referral.referredToContact.trim());
  for (const line of addresseeLines) {
    pdf.text(line, margin, y);
    y += 5;
  }
  const neededByLabel = (() => {
    if (!referral.neededBy) return '';
    const d = new Date(referral.neededBy);
    return Number.isNaN(d.getTime())
      ? referral.neededBy
      : fmtCalDate(d);
  })();
  if (neededByLabel) {
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(60, 60, 70);
    pdf.text(`Needed by: ${neededByLabel}`, margin, y);
    pdf.setFont('helvetica', 'normal');
    y += 5;
  }
  pdf.setFontSize(10);
  pdf.setTextColor(60, 60, 70);
  pdf.text(`Re: Referral for ${fullName}`, margin, y);
  y += 9;

  // Patient identity card.
  const cardH = 22;
  pdf.setFillColor(248, 250, 252);
  pdf.setDrawColor(228, 228, 232);
  pdf.setLineWidth(0.3);
  pdf.roundedRect(margin, y, contentW, cardH, 2.5, 2.5, 'FD');
  pdf.setFillColor(pr, pg, pb);
  pdf.rect(margin, y + 2, 1.4, cardH - 4, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.setTextColor(20, 20, 24);
  pdf.text(fullName, margin + 6, y + 9);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8.5);
  pdf.setTextColor(110, 110, 120);
  const idParts: string[] = [`MRN ${mrn}`];
  if (age !== null) idParts.push(`${age} y`);
  if (patient.gender)
    idParts.push(String(patient.gender).replace(/^\w/, (c) => c.toUpperCase()));
  if (patient.phone) idParts.push(String(patient.phone));
  pdf.text(idParts.join('  ·  '), margin + 6, y + 16);
  pdf.setTextColor(0, 0, 0);
  y += cardH + 8;

  // Letter body — wrapped paragraphs with page-break safety.
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10.5);
  pdf.setTextColor(28, 28, 32);
  const para = (text: string, gap = 6) => {
    const lines = pdf.splitTextToSize(text, contentW);
    if (y + lines.length * 5.5 > 268) {
      pdf.addPage();
      y = drawEditorialHeader(pdf);
    }
    pdf.text(lines, margin, y);
    y += lines.length * 5.5 + gap;
  };

  para('Dear Colleague,');
  const ageGender =
    age !== null
      ? `, a ${age}-year-old${patient.gender ? ' ' + String(patient.gender).toLowerCase() : ''} patient,`
      : '';
  para(
    `I am referring this patient${ageGender} for ${referral.specialty} evaluation and management.`,
  );
  if (referral.reason?.trim()) {
    para(`Reason for referral: ${referral.reason.trim()}`);
  }
  if (referral.clinicalContext?.trim()) {
    para(`Clinical context: ${referral.clinicalContext.trim()}`);
  }
  para(
    'I would be grateful for your assessment and ongoing input. Please do not hesitate to contact me should you require any further information.',
  );

  // Signature block.
  y += 4;
  if (y > 250) {
    pdf.addPage();
    y = drawEditorialHeader(pdf);
  }
  para('With kind regards,', 10);
  pdf.setDrawColor(150, 150, 160);
  pdf.setLineWidth(0.3);
  pdf.line(margin, y, margin + 70, y);
  y += 5;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10.5);
  pdf.setTextColor(28, 28, 32);
  if (providerName) pdf.text(providerName, margin, y);
  y += 5;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(110, 110, 120);
  pdf.text(getBrand().hospitalName, margin, y);

  drawEditorialFooter(pdf, { confidential: true });

  const safeName =
    fullName.replace(/[^a-z0-9]+/gi, '_').toLowerCase() || 'patient';
  const safeSpecialty =
    referral.specialty.replace(/[^a-z0-9]+/gi, '_').toLowerCase() || 'specialist';
  downloadAndPrintPdf(pdf, `referral_${safeName}_${safeSpecialty}.pdf`);
};

export const exportAppointmentToPDF = async (
  appointment: Appointment,
  patientName?: string,
  _language: 'en' | 'ar' = 'en'
) => {
  const pdf = new jsPDF();
  await refreshBrandAssets();

  let yPosition = drawEditorialHeader(pdf, {
    eyebrow: 'Scheduling',
    docTitle: 'Appointment',
    subtitle: patientName,
  });

  pdf.setFontSize(12);

  const addField = (label: string, value: string) => {
    pdf.setFont('helvetica', 'bold');
    pdf.text(label + ':', 20, yPosition);
    pdf.setFont('helvetica', 'normal');
    pdf.text(value, 70, yPosition);
    yPosition += 10;
  };

  const shortId = appointment.id ? appointment.id.slice(-6).toUpperCase() : '—';
  const dateLabel = (() => {
    if (!appointment.date) return '—';
    const d = new Date(appointment.date);
    return Number.isNaN(d.getTime()) ? appointment.date : fmtCalDate(d);
  })();
  const fallbackPatientName =
    appointment.patient
      ? `${appointment.patient.firstName ?? ''} ${appointment.patient.lastName ?? ''}`.trim()
      : '';
  const finalPatientName = (patientName?.trim() || fallbackPatientName || '—');
  const providerName = appointment.provider
    ? `${appointment.provider.firstName ?? ''} ${appointment.provider.lastName ?? ''}`.trim()
    : '';

  addField('Reference', shortId);
  addField('Patient', finalPatientName);
  if (providerName) addField('Provider', providerName);
  addField('Date', dateLabel);
  if (appointment.time) addField('Time', appointment.time);
  if (appointment.duration) addField('Duration', `${appointment.duration} minutes`);
  if (appointment.appointmentType) addField('Type', appointment.appointmentType);
  if (appointment.isVirtual) addField('Mode', 'Virtual');
  addField('Status', appointment.status);

  if (appointment.reason) {
    yPosition += 4;
    pdf.setFont('helvetica', 'bold');
    pdf.text('Reason for Visit:', 20, yPosition);
    yPosition += 7;
    pdf.setFont('helvetica', 'normal');
    const reasonLines = pdf.splitTextToSize(appointment.reason, 170);
    pdf.text(reasonLines, 20, yPosition);
    yPosition += reasonLines.length * 5 + 5;
  }

  if (appointment.isVirtual && appointment.virtualMeetingLink) {
    pdf.setFont('helvetica', 'bold');
    pdf.text('Meeting Link:', 20, yPosition);
    yPosition += 7;
    pdf.setFont('helvetica', 'normal');
    const linkLines = pdf.splitTextToSize(appointment.virtualMeetingLink, 170);
    pdf.text(linkLines, 20, yPosition);
    yPosition += linkLines.length * 5 + 5;
  }

  if (appointment.notes) {
    pdf.setFont('helvetica', 'bold');
    pdf.text('Notes:', 20, yPosition);
    yPosition += 7;
    pdf.setFont('helvetica', 'normal');
    const notesLines = pdf.splitTextToSize(appointment.notes, 170);
    pdf.text(notesLines, 20, yPosition);
  }

  drawEditorialFooter(pdf);
  downloadAndPrintPdf(pdf, `appointment_${shortId}.pdf`);
};

export const exportPaymentReceiptToPDF = async (receipt: {
  /** Already-localised amount string, e.g. "EGP 250.00". */
  amountFormatted: string;
  receiptNumber?: string;
  transactionId?: string;
  paymentMethod?: string;
  patientName?: string;
  date?: Date;
}) => {
  const pdf = new jsPDF();
  await refreshBrandAssets();

  let yPosition = drawEditorialHeader(pdf, {
    eyebrow: 'Billing',
    docTitle: 'Payment Receipt',
    subtitle: receipt.patientName,
  });

  pdf.setFontSize(12);
  const addField = (label: string, value: string) => {
    pdf.setFont('helvetica', 'bold');
    pdf.text(label + ':', 20, yPosition);
    pdf.setFont('helvetica', 'normal');
    pdf.text(value, 70, yPosition);
    yPosition += 10;
  };

  if (receipt.patientName) addField('Patient', receipt.patientName);
  addField('Amount Paid', receipt.amountFormatted);
  if (receipt.receiptNumber) addField('Receipt No.', receipt.receiptNumber);
  if (receipt.transactionId) addField('Transaction ID', receipt.transactionId);
  if (receipt.paymentMethod)
    addField('Method', receipt.paymentMethod.replace(/_/g, ' '));
  addField('Date', fmtDateTimeTz(receipt.date ?? new Date()));

  drawEditorialFooter(pdf);
  const receiptId =
    receipt.receiptNumber || receipt.transactionId || `${Date.now()}`;
  const safeReceiptId = receiptId.replace(/[^a-z0-9]+/gi, '_');
  downloadAndPrintPdf(pdf, `receipt_${safeReceiptId}.pdf`);
};

export const exportFilledTemplateToPDF = async (params: {
  template: Template;
  formData: Record<string, unknown>;
  richTextContent?: Record<string, string>;
  patientName?: string;
  language?: 'en' | 'ar';
}) => {
  const {
    template,
    formData,
    richTextContent = {},
    patientName,
    language = 'en',
  } = params;

  const pdf = new jsPDF();
  await refreshBrandAssets();

  const title = getLocalizedValue(template.name, language) || 'Clinical Form';
  let y = drawEditorialHeader(pdf, {
    eyebrow: 'Clinical Form',
    docTitle: title,
    subtitle: patientName,
  });

  const pageHeight = pdf.internal.pageSize.getHeight();
  const ensureSpace = (needed: number) => {
    if (y + needed > pageHeight - 18) {
      pdf.addPage();
      y = 20;
    }
  };

  const stripHtml = (html: string) =>
    html
      .replace(/<br\s*\/?>(?=)/gi, '\n')
      .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/[ \t]+/g, ' ')
      .replace(/\n\s*\n+/g, '\n')
      .trim();

  const valueFor = (field: TemplateField): string => {
    const raw = formData[field.id];
    if (raw == null || raw === '') {
      const rt = richTextContent[field.id];
      return rt ? stripHtml(rt) : '';
    }
    if (typeof raw === 'boolean') return raw ? 'Yes' : 'No';
    if (Array.isArray(raw)) return raw.map((x) => String(x)).join(', ');
    if (typeof raw === 'object') {
      const obj = raw as { headers?: unknown[]; rows?: unknown[] };
      if (Array.isArray(obj.rows)) {
        const lines: string[] = [];
        if (Array.isArray(obj.headers) && obj.headers.length) {
          lines.push(obj.headers.map((h) => String(h)).join(' | '));
        }
        obj.rows.forEach((r) => {
          const cells = Array.isArray(r) ? r : Object.values(r as object);
          lines.push(cells.map((c) => String(c)).join(' | '));
        });
        return lines.join('\n');
      }
      return JSON.stringify(raw);
    }
    return String(raw);
  };

  (template.sections || []).forEach((section: TemplateSection) => {
    const sectionTitle = getLocalizedValue(section.title, language);
    if (sectionTitle) {
      ensureSpace(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(13);
      pdf.text(sectionTitle, 20, y);
      y += 8;
    }

    (section.fields || []).forEach((field: TemplateField) => {
      // A 'section' field is a visual sub-heading, not a value.
      if (field.type === 'section') {
        ensureSpace(9);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.text(getLocalizedValue(field.name, language) || '', 20, y);
        y += 7;
        return;
      }

      const value = valueFor(field);
      if (!value) return; // omit empty fields from the printed document

      const label = getLocalizedValue(field.name, language) || '';
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      const labelLines = pdf.splitTextToSize(`${label}:`, 170);
      ensureSpace(labelLines.length * 5 + 6);
      pdf.text(labelLines, 20, y);
      y += labelLines.length * 5 + 1;

      pdf.setFont('helvetica', 'normal');
      const valueLines = pdf.splitTextToSize(value, 170);
      ensureSpace(valueLines.length * 5 + 4);
      pdf.text(valueLines, 20, y);
      y += valueLines.length * 5 + 4;
    });

    y += 3;
  });

  drawEditorialFooter(pdf);
  const safeTitle = (title || 'form').replace(/[^a-z0-9]+/gi, '_').toLowerCase();
  downloadAndPrintPdf(pdf, `${safeTitle}_${Date.now()}.pdf`);
};

export const exportAnalysisResultToPDF = async (analysis: {
  analysisType: string;
  summary?: string;
  recommendations?: string[];
  risks?: Array<{ severity: string; factor: string; description: string }>;
  content?: string;
}) => {
  const pdf = new jsPDF();
  await refreshBrandAssets();

  const pretty = analysis.analysisType
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());

  let y = drawEditorialHeader(pdf, {
    eyebrow: 'AI Analysis',
    docTitle: `${pretty} Analysis`,
  });

  const pageHeight = pdf.internal.pageSize.getHeight();
  const ensureSpace = (needed: number) => {
    if (y + needed > pageHeight - 18) {
      pdf.addPage();
      y = 20;
    }
  };
  const heading = (text: string) => {
    ensureSpace(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(13);
    pdf.text(text, 20, y);
    y += 8;
  };
  const paragraph = (text: string) => {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);
    const lines = pdf.splitTextToSize(text, 170);
    ensureSpace(lines.length * 5 + 4);
    pdf.text(lines, 20, y);
    y += lines.length * 5 + 4;
  };

  if (analysis.summary) {
    heading('Summary');
    paragraph(analysis.summary);
  }

  if (analysis.recommendations?.length) {
    heading('Recommendations');
    analysis.recommendations.forEach((rec) => paragraph(`•  ${rec}`));
  }

  if (analysis.risks?.length) {
    heading('Risk Assessment');
    analysis.risks.forEach((risk) =>
      paragraph(
        `${risk.severity.toUpperCase()} — ${risk.factor}: ${risk.description}`,
      ),
    );
  }

  if (analysis.content) {
    heading('Detailed Analysis');
    paragraph(analysis.content);
  }

  drawEditorialFooter(pdf);
  downloadAndPrintPdf(pdf, `analysis_${analysis.analysisType}_${Date.now()}.pdf`);
};

interface ComplianceReportInput {
  id: string;
  name: string;
  type: string;
  periodStart: string;
  periodEnd: string;
  totalEntries: number;
  violations: number;
  generatedAt?: string;
  generatedBy?: { firstName: string; lastName: string };
  summary?: Record<string, unknown>;
}

const COMPLIANCE_LABELS: Record<string, { title: string; standard: string; reference: string }> = {
  hipaa: {
    title: 'HIPAA Compliance Audit',
    standard: 'Health Insurance Portability and Accountability Act',
    reference:
      'This audit is prepared in accordance with the HIPAA Security Rule (45 CFR §164.312) and Privacy Rule (45 CFR §164.528). It documents system access events involving Protected Health Information (PHI), including viewing, creation, modification, and disclosure activity recorded during the period under review.',
  },
  gdpr: {
    title: 'GDPR Data Processing Audit',
    standard: 'EU General Data Protection Regulation',
    reference:
      'This audit is prepared in accordance with Articles 5, 30, and 32 of the GDPR. It documents personal-data processing activities, lawful-basis associations, access events, and data-subject-rights exercises recorded during the period under review.',
  },
  custom: {
    title: 'Custom Compliance Audit',
    standard: 'Internal Compliance Policy',
    reference:
      'This is an internal compliance audit prepared against organisation-defined criteria. The findings reflect access events, data handling, and configuration changes captured by the platform audit log during the reporting period.',
  },
};

const drawKpiCard = (
  pdf: jsPDF,
  x: number,
  y: number,
  w: number,
  h: number,
  label: string,
  value: string,
  accent: [number, number, number]
) => {
  // Card background
  pdf.setFillColor(250, 250, 252);
  pdf.setDrawColor(225, 225, 232);
  pdf.setLineWidth(0.2);
  pdf.roundedRect(x, y, w, h, 2, 2, 'FD');

  // Accent bar at top
  pdf.setFillColor(accent[0], accent[1], accent[2]);
  pdf.roundedRect(x, y, w, 1.5, 2, 2, 'F');

  // Label
  pdf.setTextColor(110, 110, 120);
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'bold');
  pdf.text(label.toUpperCase(), x + 4, y + 6);

  // Value
  pdf.setTextColor(20, 20, 24);
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.text(value, x + 4, y + h - 4);
};

export const exportComplianceReportToPDF = async (
  report: ComplianceReportInput,
  _logs?: unknown[]
) => {
  const pdf = new jsPDF();
  await refreshBrandAssets();

  const typeKey = (report.type || '').toLowerCase();
  const meta = COMPLIANCE_LABELS[typeKey] || {
    title: 'Compliance Audit',
    standard: 'Compliance Review',
    reference:
      'This compliance audit tracks system access and data handling activities recorded by the platform audit log during the period under review.',
  };

  const periodLabel = `${fmtDateTz(report.periodStart)} – ${fmtDateTz(report.periodEnd)}`;

  let y = drawEditorialHeader(pdf, {
    eyebrow: 'Compliance',
    docTitle: meta.title,
    subtitle: `${report.name} · ${periodLabel}`,
    showConfidential: true,
  });

  const margin = 14;
  const pageW = pdf.internal.pageSize.getWidth();
  const contentW = pageW - margin * 2;

  // KPI cards
  const cardGap = 4;
  const cardW = (contentW - cardGap * 2) / 3;
  const cardH = 22;

  const complianceRate =
    report.totalEntries > 0
      ? ((1 - report.violations / Math.max(report.totalEntries, 1)) * 100).toFixed(1)
      : '100.0';
  const rateNum = parseFloat(complianceRate);
  const rateAccent: [number, number, number] =
    rateNum >= 99 ? [16, 185, 129] : rateNum >= 95 ? [234, 179, 8] : [220, 38, 38];

  drawKpiCard(pdf, margin, y, cardW, cardH, 'Audit Entries', String(report.totalEntries), [99, 102, 241]);
  drawKpiCard(
    pdf,
    margin + cardW + cardGap,
    y,
    cardW,
    cardH,
    'Violations',
    String(report.violations),
    report.violations > 0 ? [220, 38, 38] : [16, 185, 129]
  );
  drawKpiCard(
    pdf,
    margin + (cardW + cardGap) * 2,
    y,
    cardW,
    cardH,
    'Compliance Rate',
    `${complianceRate}%`,
    rateAccent
  );
  y += cardH + 10;

  // Section helper
  const sectionHeading = (title: string) => {
    if (y > 250) {
      pdf.addPage();
      y = drawEditorialHeader(pdf);
    }
    const [pr, pg, pb] = getBrand().primary;
    pdf.setFillColor(pr, pg, pb);
    pdf.rect(margin, y, 1.2, 6, 'F');
    pdf.setTextColor(30, 30, 30);
    pdf.setFontSize(13);
    pdf.setFont('helvetica', 'bold');
    pdf.text(title, margin + 4, y + 5);
    y += 9;
    pdf.setTextColor(0, 0, 0);
  };

  const labelValue = (label: string, value: string) => {
    if (y > 270) {
      pdf.addPage();
      y = drawEditorialHeader(pdf);
    }
    pdf.setFontSize(10);
    pdf.setTextColor(110, 110, 120);
    pdf.setFont('helvetica', 'bold');
    pdf.text(label, margin, y);
    pdf.setTextColor(25, 25, 30);
    pdf.setFont('helvetica', 'normal');
    const lines = pdf.splitTextToSize(value, contentW - 50);
    pdf.text(lines, margin + 50, y);
    y += Math.max(5, lines.length * 5) + 1;
  };

  sectionHeading('Report Information');
  labelValue('Report ID', report.id);
  labelValue('Standard', meta.standard);
  labelValue('Reporting Period', periodLabel);
  if (report.generatedBy) {
    labelValue('Prepared By', `${report.generatedBy.firstName} ${report.generatedBy.lastName}`);
  }
  if (report.generatedAt) {
    labelValue('Generated', fmtDateTimeTz(report.generatedAt));
  }
  y += 4;

  // Summary
  if (report.summary && Object.keys(report.summary).length > 0) {
    sectionHeading('Executive Summary');
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    Object.entries(report.summary).forEach(([key, value]) => {
      const label = key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (s) => s.toUpperCase())
        .trim();
      const valueStr =
        typeof value === 'object' && value !== null ? JSON.stringify(value, null, 2) : String(value);
      labelValue(label, valueStr);
    });
    y += 4;
  }

  // Standards reference
  sectionHeading('Compliance Standards');
  pdf.setFontSize(10);
  pdf.setTextColor(45, 45, 55);
  pdf.setFont('helvetica', 'normal');
  const refLines = pdf.splitTextToSize(meta.reference, contentW);
  pdf.text(refLines, margin, y);
  y += refLines.length * 5 + 6;

  // Findings
  sectionHeading('Key Findings');
  pdf.setFontSize(10);
  pdf.setTextColor(30, 30, 35);
  pdf.setFont('helvetica', 'normal');
  const findings = [
    `${report.totalEntries.toLocaleString()} audit log entries analysed during the reporting period.`,
    `${report.violations.toLocaleString()} compliance violation${report.violations === 1 ? '' : 's'} flagged for review.`,
    `Effective compliance rate: ${complianceRate}%${
      rateNum >= 99
        ? ' — within target band.'
        : rateNum >= 95
          ? ' — borderline; review violation cluster.'
          : ' — below target; remediation plan required.'
    }`,
  ];
  findings.forEach((f) => {
    if (y > 270) {
      pdf.addPage();
      y = drawEditorialHeader(pdf);
    }
    const lines = pdf.splitTextToSize(`• ${f}`, contentW - 4);
    pdf.text(lines, margin + 2, y);
    y += lines.length * 5 + 1;
  });
  y += 6;

  // Attestation block
  if (y > 235) {
    pdf.addPage();
    y = drawEditorialHeader(pdf);
  }
  sectionHeading('Attestation');
  pdf.setFontSize(9);
  pdf.setTextColor(70, 70, 80);
  pdf.setFont('helvetica', 'italic');
  const attestation =
    'I certify that the information contained in this report has been generated from the platform audit log without modification and reflects the events recorded during the period under review.';
  const attLines = pdf.splitTextToSize(attestation, contentW);
  pdf.text(attLines, margin, y);
  y += attLines.length * 4.5 + 14;

  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(60, 60, 70);
  pdf.setDrawColor(180, 180, 190);
  pdf.setLineWidth(0.3);

  const sigW = (contentW - 10) / 2;
  pdf.line(margin, y, margin + sigW, y);
  pdf.line(margin + sigW + 10, y, margin + contentW, y);
  pdf.setFontSize(8);
  pdf.text('Compliance Officer Signature', margin, y + 4);
  pdf.text('Date', margin + sigW + 10, y + 4);

  drawEditorialFooter(pdf, { confidential: true });

  const safeName = report.name.replace(/[^a-z0-9]+/gi, '_').toLowerCase() || 'compliance_report';
  pdf.save(`${safeName}_${typeKey || 'audit'}.pdf`);
};

/**
 * Export imaging order to PDF
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const exportImagingOrderToPDF = async (order: any, patientName: string, _language: 'en' | 'ar' = 'en') => {
  const pdf = new jsPDF();
  await refreshBrandAssets();

  let yPosition = drawEditorialHeader(pdf, {
    eyebrow: 'Imaging',
    docTitle: 'Imaging Order',
    subtitle: patientName,
  });

  // Order details
  pdf.setFontSize(12);

  const addField = (label: string, value: string) => {
    pdf.setFont('helvetica', 'bold');
    pdf.text(label + ':', 20, yPosition);
    pdf.setFont('helvetica', 'normal');
    pdf.text(value || 'N/A', 70, yPosition);
    yPosition += 10;
  };

  addField('Order ID', order.id?.substring(0, 8) || '');
  addField('Patient', patientName);
  addField('Order Date', order.orderDate ? fmtCalDate(order.orderDate) : '');
  addField('Status', order.status);
  addField('Priority', order.priority);
  addField('Imaging Type', order.imagingType);
  addField('Body Part', order.bodyPart);
  addField('Laterality', order.laterality);

  if (order.imagingCenter) {
    addField('Imaging Center', order.imagingCenter);
  }
  if (order.icdCode) {
    addField('ICD Code', order.icdCode);
  }
  if (order.scheduledDate) {
    addField('Scheduled Date', fmtCalDate(order.scheduledDate));
  }
  if (order.completedDate) {
    addField('Completed Date', fmtCalDate(order.completedDate));
  }

  // Clinical Indication
  if (order.clinicalIndication) {
    yPosition += 5;
    pdf.setFont('helvetica', 'bold');
    pdf.text('Clinical Indication:', 20, yPosition);
    yPosition += 7;
    pdf.setFont('helvetica', 'normal');
    const lines = pdf.splitTextToSize(order.clinicalIndication, 170);
    pdf.text(lines, 20, yPosition);
    yPosition += lines.length * 5 + 5;
  }

  // Provisional Diagnosis
  if (order.provisionalDiagnosis) {
    pdf.setFont('helvetica', 'bold');
    pdf.text('Provisional Diagnosis:', 20, yPosition);
    yPosition += 7;
    pdf.setFont('helvetica', 'normal');
    const lines = pdf.splitTextToSize(order.provisionalDiagnosis, 170);
    pdf.text(lines, 20, yPosition);
    yPosition += lines.length * 5 + 5;
  }

  // Report Notes
  if (order.reportNotes) {
    pdf.setFont('helvetica', 'bold');
    pdf.text('Report Notes:', 20, yPosition);
    yPosition += 7;
    pdf.setFont('helvetica', 'normal');
    const lines = pdf.splitTextToSize(order.reportNotes, 170);
    pdf.text(lines, 20, yPosition);
    yPosition += lines.length * 5 + 5;
  }

  // Review status
  yPosition += 5;
  addField('Reviewed', order.reviewed ? 'Yes' : 'No');

  // Footer
  drawEditorialFooter(pdf);

  downloadAndPrintPdf(pdf, `imaging_order_${order.id?.substring(0, 8) || 'unknown'}.pdf`);
};

interface OrderForPdf {
  id: string;
  type: string;
  status: string;
  priority?: string;
  orderedBy?: { name?: string; title?: string };
  orderedAt?: Date | string;
  startDate?: Date | string;
  endDate?: Date | string;
  indication?: { en?: string; ar?: string } | string;
  notes?: { en?: string; ar?: string } | string;
}

export const exportOrderToPDF = async (
  order: OrderForPdf,
  patientName: string,
  language: 'en' | 'ar' = 'en'
) => {
  const pdf = new jsPDF();
  await refreshBrandAssets();
  let yPosition = drawEditorialHeader(pdf, {
    eyebrow: 'Order',
    docTitle: 'Order Summary',
    subtitle: patientName,
  });

  pdf.setFontSize(12);
  const addField = (label: string, value: string) => {
    pdf.setFont('helvetica', 'bold');
    pdf.text(label + ':', 20, yPosition);
    pdf.setFont('helvetica', 'normal');
    pdf.text(value, 70, yPosition);
    yPosition += 10;
  };

  const fmtDate = (d?: Date | string) => {
    if (!d) return '';
    const v = typeof d === 'string' ? new Date(d) : d;
    return Number.isNaN(v.getTime()) ? '' : fmtDateTimeTz(v);
  };

  addField('Order ID', order.id ? order.id.slice(0, 8).toUpperCase() : '—');
  addField('Patient', patientName || '—');
  addField('Type', order.type);
  addField('Status', order.status);
  if (order.priority) addField('Priority', order.priority);
  const ordered = fmtDate(order.orderedAt);
  if (ordered) addField('Ordered At', ordered);
  if (order.orderedBy?.name) {
    const title = order.orderedBy.title ? `, ${order.orderedBy.title}` : '';
    addField('Ordered By', `${order.orderedBy.name}${title}`);
  }
  const start = fmtDate(order.startDate);
  if (start) addField('Start Date', start);
  const end = fmtDate(order.endDate);
  if (end) addField('End Date', end);

  const indicationText =
    typeof order.indication === 'string'
      ? order.indication
      : getLocalizedValue(order.indication as MultilingualField | undefined, language);
  if (indicationText) {
    yPosition += 4;
    pdf.setFont('helvetica', 'bold');
    pdf.text('Clinical Indication:', 20, yPosition);
    yPosition += 7;
    pdf.setFont('helvetica', 'normal');
    const lines = pdf.splitTextToSize(indicationText, 170);
    pdf.text(lines, 20, yPosition);
    yPosition += lines.length * 5 + 5;
  }

  const notesText =
    typeof order.notes === 'string'
      ? order.notes
      : getLocalizedValue(order.notes as MultilingualField | undefined, language);
  if (notesText) {
    pdf.setFont('helvetica', 'bold');
    pdf.text('Notes:', 20, yPosition);
    yPosition += 7;
    pdf.setFont('helvetica', 'normal');
    const lines = pdf.splitTextToSize(notesText, 170);
    pdf.text(lines, 20, yPosition);
    yPosition += lines.length * 5 + 5;
  }

  drawEditorialFooter(pdf);
  downloadAndPrintPdf(pdf, `order_${order.id ? order.id.slice(0, 8) : 'unknown'}.pdf`);
};

interface ScheduleTimeBlock {
  startTime: string;
  endTime: string;
  notes?: string;
}

interface ProviderScheduleEntry {
  providerName: string;
  weeklyHours?: number;
  daysOfWeek: Array<{ day: string; blocks: ScheduleTimeBlock[] }>;
}

const computeBlockHours = (b: ScheduleTimeBlock): number => {
  const [sh, sm] = b.startTime.split(':').map(Number);
  const [eh, em] = b.endTime.split(':').map(Number);
  if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return 0;
  return eh - sh + (em - sm) / 60;
};

export const exportProviderScheduleToPDF = async (entries: ProviderScheduleEntry[]) => {
  if (!entries.length) return;
  const pdf = new jsPDF();
  await refreshBrandAssets();
  let yPosition = drawEditorialHeader(pdf, {
    eyebrow: 'Scheduling',
    docTitle: 'Provider Schedule',
  });

  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'italic');
  pdf.text(`Generated ${fmtDateTimeTz(new Date())}`, 20, yPosition);
  yPosition += 10;

  entries.forEach((entry, idx) => {
    if (yPosition > 250) {
      pdf.addPage();
      yPosition = drawEditorialHeader(pdf);
    }

    if (idx > 0) yPosition += 4;

    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text(entry.providerName || '—', 20, yPosition);

    const weekly =
      entry.weeklyHours ??
      entry.daysOfWeek.reduce(
        (sum, d) => sum + d.blocks.reduce((s, b) => s + computeBlockHours(b), 0),
        0
      );
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`${weekly.toFixed(1)} hrs / week`, 190, yPosition, { align: 'right' });
    yPosition += 6;

    pdf.setLineWidth(0.2);
    pdf.line(20, yPosition, 190, yPosition);
    yPosition += 6;

    pdf.setFontSize(11);
    entry.daysOfWeek.forEach(({ day, blocks }) => {
      if (yPosition > 275) {
        pdf.addPage();
        yPosition = drawEditorialHeader(pdf);
      }
      pdf.setFont('helvetica', 'bold');
      const dayLabel = day.charAt(0).toUpperCase() + day.slice(1);
      pdf.text(dayLabel, 20, yPosition);
      pdf.setFont('helvetica', 'normal');
      if (!blocks.length) {
        pdf.setTextColor(150);
        pdf.text('Off', 60, yPosition);
        pdf.setTextColor(0);
      } else {
        const text = blocks
          .map((b) => `${b.startTime}–${b.endTime}${b.notes ? ` (${b.notes})` : ''}`)
          .join(', ');
        const lines = pdf.splitTextToSize(text, 130);
        pdf.text(lines, 60, yPosition);
        yPosition += (lines.length - 1) * 5;
      }
      yPosition += 7;
    });
  });

  drawEditorialFooter(pdf);
  const filename =
    entries.length === 1
      ? `schedule_${entries[0].providerName.replace(/[^a-z0-9]+/gi, '_').toLowerCase()}.pdf`
      : `schedule_all_providers.pdf`;
  pdf.save(filename);
};